import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { historyQuestionsService } from '../../services/historyQuestionsService';
import Spinner from '../common/Spinner';

const AnswersModal = ({ isOpen, onClose, question }) => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isOpen && question) {
      fetchAnswers();
    }
  }, [isOpen, question]);

  const fetchAnswers = async () => {
    try {
      setLoading(true);
      const response = await historyQuestionsService.getAnswersByQuestion(question.id);
      setAnswers(response.data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching answers:', error);
      setAnswers([]);
    } finally {
      setLoading(false);
    }
  };

  const formatAnswerValue = (answer, questionType) => {
    if (!answer) return '-';

    if (Array.isArray(answer)) {
      return answer.join(', ');
    }

    if (typeof answer === 'object' && answer.value !== undefined) {
      return answer.value;
    }

    return answer;
  };

  const getOptionLabel = (value) => {
    if (!question.options) return value;
    const option = question.options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const totalPages = Math.ceil(answers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentAnswers = answers.slice(startIndex, startIndex + itemsPerPage);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 flex justify-between items-center border-b">
          <div>
            <h2 className="text-xl font-bold">Answers for Question</h2>
            <p className="text-orange-100 text-sm mt-1">{question.question_text}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-orange-800 p-2 rounded-lg transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          ) : answers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-gray-400 mb-3">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-500 font-medium">No answers yet</p>
              <p className="text-gray-400 text-sm">Users haven't answered this question</p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                  <p className="text-gray-600 text-sm font-medium">Total Answers</p>
                  <p className="text-3xl font-bold text-orange-600">{answers.length}</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                  <p className="text-gray-600 text-sm font-medium">Unique Users</p>
                  <p className="text-3xl font-bold text-green-600">
                    {new Set(answers.map(a => a.user_id)).size}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                  <p className="text-gray-600 text-sm font-medium">Sessions</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {new Set(answers.map(a => a.session_id)).size}
                  </p>
                </div>
              </div>

              {/* Answers Table */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">User ID</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Answer</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Session</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAnswers.map((answer, idx) => {
                      const answerValue = formatAnswerValue(answer.answer_value, question.question_type);
                      const displayValue = question.question_type === 'single_select' || question.question_type === 'multi_select'
                        ? (Array.isArray(answerValue) ? answerValue.map(v => getOptionLabel(v)).join(', ') : getOptionLabel(answerValue))
                        : answerValue;

                      return (
                        <tr key={answer.id} className="border-b hover:bg-orange-50 transition-colors">
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full text-xs font-semibold">
                              {answer.user_id}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="font-medium text-gray-900 break-words">{displayValue}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                              {answer.session_id}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {answer.is_active ? (
                              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                Active
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                                Inactive
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 py-4 mt-4 border-t">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 border rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white border-orange-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnswersModal;
